/**
 * The settings that cascade, and what a site ends up with.
 *
 * Only settings the engine itself reads live here. Contact names, addresses
 * and the rest of what a settings screen shows are display data; they cascade
 * by the same `resolve()` but the engine never has to understand them.
 */

import { resolve } from "./cascade.js";
import { DEFAULT_DIGEST_HOUR } from "./digest.js";
import { DEFAULT_PREVIEW_DEPARTMENTS } from "./week.js";
import { MONDAY_TO_FRIDAY, type Calendar, type IsoDate, type Weekday } from "./dates.js";

export type DeadlineUnit = "business" | "calendar";

/** What any one level -- Global, Company, Site -- may set. Every field optional: blank inherits. */
export interface SettingsLevel {
  timeZone?: string;
  workdays?: Weekday[];
  holidays?: IsoDate[];
  /**
   * The unit a template's day offsets are counted in when the task itself does
   * not say. A site that runs 24/7 switches this to calendar (PRD 6.2).
   */
  deadlineUnit?: DeadlineUnit;
  /** Compliance flags a template's conditions can test, and that change what completing a task asks for. */
  hipaa?: boolean;
  /** The hour of the morning, 0 to 23 in the site's zone, lateness is told in one summary. */
  digestHour?: number;
  /** The departments mailed next week's starters and leavers, on the last working day of the week. An empty list mails nobody. */
  previewDepartments?: string[];
  /** The US state (USPS code) the site is in, for the rules that differ by state (final pay). Blank is unknown. */
  jurisdiction?: string;
}

export interface SiteSettings {
  timeZone: string;
  calendar: Calendar;
  deadlineUnit: DeadlineUnit;
  hipaa: boolean;
  digestHour: number;
  previewDepartments: string[];
  jurisdiction: string;
}

/**
 * What a brand-new organisation starts from before anybody types anything.
 * UTC rather than a guess at the owner's zone: a wrong guess is invisible until
 * a deadline lands a few hours off, and the Global settings screen asks for a
 * zone first.
 */
export const PLATFORM_DEFAULTS: Required<SettingsLevel> = {
  timeZone: "UTC",
  workdays: [...MONDAY_TO_FRIDAY],
  holidays: [],
  deadlineUnit: "business",
  hipaa: false,
  digestHour: DEFAULT_DIGEST_HOUR,
  previewDepartments: [...DEFAULT_PREVIEW_DEPARTMENTS],
  jurisdiction: "",
};

/**
 * One site's settings, from its levels outermost first (Global, Company, Site).
 *
 * Holidays are the one setting that *accumulates* rather than overrides: a
 * site's regional holiday is in addition to the company's, not instead of
 * them (PRD 6.2, "can differ by site (e.g., regional holidays)"). Everything
 * else is nearest-wins.
 */
export function siteSettings(levels: readonly SettingsLevel[]): SiteSettings {
  const resolved = { ...PLATFORM_DEFAULTS, ...resolve<SettingsLevel>(levels) };
  const holidays = new Set<IsoDate>();
  for (const level of levels) for (const day of level.holidays ?? []) holidays.add(day);

  return {
    timeZone: resolved.timeZone,
    calendar: { workdays: resolved.workdays, holidays: [...holidays].sort() },
    deadlineUnit: resolved.deadlineUnit,
    hipaa: resolved.hipaa,
    digestHour: resolved.digestHour,
    previewDepartments: resolved.previewDepartments,
    jurisdiction: resolved.jurisdiction,
  };
}
